import { formatCompanyDate } from './companyDateFormat';

/** Extract yyyy-MM-dd from API datetime strings without timezone conversion. */
export function extractDatePart(datetimeString: string | null | undefined): string {
  if (!datetimeString) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(datetimeString)) return datetimeString;

  const tPart = datetimeString.split('T')[0];
  if (tPart && /^\d{4}-\d{2}-\d{2}$/.test(tPart)) return tPart;

  const spacePart = datetimeString.split(' ')[0];
  return spacePart && /^\d{4}-\d{2}-\d{2}$/.test(spacePart) ? spacePart : '';
}

const DEFAULT_DATE_ONLY_DISPLAY: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

/** Display a calendar date from API values without UTC timezone shift. */
export function formatDateOnly(
  datetimeString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale?: string | string[]
): string {
  const datePart = extractDatePart(datetimeString);
  if (!datePart) return '';

  if (!options && !locale) {
    return formatCompanyDate(datePart);
  }

  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return '';

  return new Date(year, month - 1, day).toLocaleDateString(
    locale,
    options ?? DEFAULT_DATE_ONLY_DISPLAY
  );
}

/**
 * Extract HH:mm from API datetime strings without timezone conversion.
 * Matches Attendance page behaviour — important when API marks Unspecified SQL
 * datetimes as UTC (…Z) while timecard day fields use plain strings.
 */
export function extractTimezoneNaiveTime(datetimeString: string | null | undefined): string {
  if (!datetimeString) return '';
  try {
    if (/^\d{2}:\d{2}$/.test(datetimeString)) return datetimeString;

    let timePart = datetimeString.split('T')[1];
    if (!timePart) {
      const spaceIndex = datetimeString.indexOf(' ');
      if (spaceIndex > 0) {
        timePart = datetimeString.substring(spaceIndex + 1);
      }
    }
    if (!timePart) return '';

    const [hours, minutes] = timePart.split(':');
    if (!hours || !minutes) return '';

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  } catch {
    return '';
  }
}

/** Display helper: returns "--:--" when time is missing. */
export function formatDisplayTime(datetimeString: string | null | undefined): string {
  const time = extractTimezoneNaiveTime(datetimeString);
  return time || '--:--';
}

/** Sortable yyyy-MM-ddTHH:mm for range checks (handles overnight spans). */
export function toSortableDateTime(datetimeString: string | null | undefined): string {
  if (!datetimeString) return '';
  const date = extractDatePart(datetimeString);
  const time = extractTimezoneNaiveTime(datetimeString);
  if (!date || !time) return '';
  return `${date}T${time}`;
}

/**
 * Format an instant as timezone-naive ISO (no Z) in the location's IANA/Windows timezone.
 * Use when sending punch times to the attendance API.
 */
export function toWallClockIsoInTimeZone(instant: Date, timeZone: string): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
}

/** Build naive API datetime from calendar date + HH:mm (no timezone suffix). */
export function toWallClockIsoFromDateAndTime(date: string, time: string): string {
  const normalizedTime = time.length === 5 ? `${time}:00` : time;
  return `${date}T${normalizedTime}`;
}

export function compareWallClock(a: string | null | undefined, b: string | null | undefined): number {
  return toSortableDateTime(a).localeCompare(toSortableDateTime(b));
}

function wallClockToMsFromApi(value: string): number {
  const date = extractDatePart(value);
  const time = extractTimezoneNaiveTime(value);
  if (!date || !time) return 0;
  const [y, m, d] = date.split('-').map(Number);
  const [h, min] = time.split(':').map(Number);
  return new Date(y, m - 1, d, h, min, 0).getTime();
}

/** Hours between two wall-clock punch times (handles overnight clock-out). */
export function wallClockDiffHours(clockIn: string, clockOut: string): number {
  const inMs = wallClockToMsFromApi(clockIn);
  let outMs = wallClockToMsFromApi(clockOut);
  if (!inMs || !outMs) return 0;
  if (outMs < inMs) outMs += 24 * 60 * 60 * 1000;
  return (outMs - inMs) / (1000 * 60 * 60);
}
